import { Flex, Text } from "@mantine/core";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import DataTable, { Column } from "../../common/Table/DataTable";
import TagBadges from "../../common/TagBadges";
import TableDeleteButton from "../../common/Buttons/TableDeleteButton";
import TableEditButton from "../../common/Buttons/TableEditButton";
import { useTableQuery } from "../../../hooks/useTableQuery";
import { useDeleteWithConfirm } from "../../../hooks/useDeleteWithConfirm";
import { deleteTag, getTagList, TagKind } from "../../../api/tagApi";
import { useAuth } from "../../../context/AuthContext";

const KIND_LABELS: Record<TagKind, string> = {
  role: "Rol",
  skill: "Yetenek",
};

const TagSettingsTable = ({ kind }: { kind: TagKind }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchTagSelectData } = useAuth();
  const label = KIND_LABELS[kind];

  const baseParams = useMemo(() => ({ kind }), [kind]);

  const table = useTableQuery({
    fetcher: getTagList,
    baseParams,
    initialLimit: 10,
  });

  const onDeleted = useCallback(() => {
    table.refetch();
    refetchTagSelectData();
  }, [table, refetchTagSelectData]);

  const { askDelete, modal } = useDeleteWithConfirm({
    deleteFn: deleteTag,
    onDeleted,
    title: `${label} Sil`,
    text: `Bu ${label.toLowerCase()} kaydını silmek istediğinizden emin misiniz?`,
  });

  const columns: Column[] = [
    {
      key: "name",
      header: `${label} İsmi`,
      width: "20%",
      render: (row) => <TagBadges tags={[{ ...row, kind }]} />,
    },
    {
      key: "description",
      header: "Açıklama",
      width: kind === "role" ? "40%" : "65%",
      render: (row) => (
        <Text size="md" fw={400} style={{ lineHeight: "1.5", color: "#111827" }}>
          {row?.description || "Açıklama belirtilmemiş."}
        </Text>
      ),
    },
    ...(kind === "role"
      ? [
          {
            key: "relatedTags",
            header: "Gerekli Yetenekler",
            width: "25%",
            render: (row: any) => (
              <TagBadges
                tags={row?.relatedTags}
                max={2}
                emptyText="Yetenek bilgisi yok"
              />
            ),
          },
        ]
      : []),
    {
      key: "actions",
      header: "İşlemler",
      width: "15%",
      render: (row) => (
        <Flex gap={6} justify="start">
          <TableEditButton
            onClick={(e: any) => {
              e.stopPropagation();
              navigate(`${location.pathname}/${row?._id}/update?kind=${kind}`);
            }}
          />
          <TableDeleteButton
            onClick={(e: any) => {
              e.stopPropagation();
              askDelete(row);
            }}
          />
        </Flex>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={table.rows}
        loading={table.loading}
        emptyText={`Henüz ${label.toLowerCase()} kaydı yok.`}
        searchValue={table.search}
        onSearchChange={table.setSearch}
        searchPlaceholder={`${label} ara...`}
        page={table.page}
        totalPages={table.totalPages}
        onPageChange={table.setPage}
        limit={table.limit}
        onLimitChange={table.setLimit}
      />
      {modal}
    </>
  );
};

export default TagSettingsTable;
